import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { useTranslation } from 'react-i18next';
import useQueryFilter from 'hooks/useQueryFilter';
import useCallbackRef from 'hooks/useCallbackRef';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import utils, { getUserFullNameOrEmail, toggleValueInArray } from 'utils';
import { Button, Checkbox } from 'ncoded-component-library';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import UserIcon from 'icons/User.icon';
import { NAVIGATION_VARIANTS, NavigationVariants } from 'models/Navigation';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';

import './MemberFilter.styles.scss';

type MemberFilterProps = {
  className?: string;
  variant?: NavigationVariants;
};

const FILTER_KEY = 'dataRoomMemberId';

const getMembersOption = (members: DataRoomMember[]) =>
  members.map((member) => ({
    value: member._id,
    label: getUserFullNameOrEmail(member),
  }));

const MemberFilter: React.FC<MemberFilterProps> = (props) => {
  const { className, variant = NAVIGATION_VARIANTS.SECONDARY } = props;

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);

  const { buySideMembers, sellSideMembers } = useContext(MembersContext);

  const { t } = useTranslation();

  const {
    params: { dataRoomMemberId },
    onFiltersApply,
  } = useQueryFilter();

  const defaultVal = useMemo(
    () => (dataRoomMemberId ? utils.mapParamsToArray(dataRoomMemberId) : []),
    [dataRoomMemberId],
  );

  const [currentState, setCurrentState] = useState(defaultVal);

  const classes = classNames('member-filter', className);

  const triggerTitle = useMemo(
    () => t(dataRoomMemberId ? 'openToSeeMemberFilters' : 'member'),
    [dataRoomMemberId, t],
  );

  const onSelectFilters = useCallback(
    (options: string[]) => {
      const patchedVal = utils.multiSelectValueToStringArray(options as any);

      dropdown.setIsOpen(false);
      onFiltersApply(FILTER_KEY, patchedVal);
    },
    [dropdown, onFiltersApply],
  );

  const handleToggleAll = useCallback(
    (members: DataRoomMember[], areAllMembersChecked: boolean) =>
      setCurrentState((prev) => {
        const memberValues = members.map(({ _id }) => _id);
        const otherValues = prev.filter((v) => !memberValues.includes(v));

        return areAllMembersChecked
          ? otherValues
          : [...otherValues, ...memberValues];
      }),
    [],
  );

  const renderSideOptions = useCallback(
    (members: DataRoomMember[], label: string) => {
      const areAllMembersChecked = members.every(({ _id }) =>
        currentState.includes(_id),
      );

      return (
        <>
          <p>{t(label)}</p>
          <Checkbox
            label={t('includeAll')}
            checked={areAllMembersChecked}
            onChange={() => handleToggleAll(members, areAllMembersChecked)}
          />
          {getMembersOption(members).map(({ value, label }) => (
            <Checkbox
              key={value}
              label={label}
              checked={currentState.some((el) => el === value)}
              onChange={() => {
                setCurrentState((prev) => toggleValueInArray(prev, value));
              }}
            />
          ))}
        </>
      );
    },
    [currentState, handleToggleAll, t],
  );

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      trigerTitle={triggerTitle}
      icon={UserIcon}
      variant={variant}
      onClose={() => setCurrentState(defaultVal)}
    >
      <div>
        {renderSideOptions(sellSideMembers, 'sellSide')}
        {!!buySideMembers?.length &&
          renderSideOptions(buySideMembers, 'buySide')}
      </div>

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={currentState === defaultVal}
          onClick={() => onSelectFilters(currentState)}
        >
          {t('applyMemberFilter')}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default MemberFilter;
